import { slugifyTicketType } from './slugify';

/**
 * Slugify an item name
 */
function slugifyItemName(itemName) {
  return (itemName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Resolve the icon URL for a ticket based on icon configuration.
 * Resolution order:
 *   1. First matching subtype rule's icon (on the type)
 *   2. Type-level icon (specific project+serviceType+itemName override)
 *   3. Item name group icon
 *   4. Default icon
 *   5. null (render colored dot)
 *
 * @param {object} ticket - Ticket object with projectName, serviceType, itemName, etc.
 * @param {object} iconConfig - { defaultIconUrl, items: [...], types: [...] }
 * @returns {{ iconUrl: string|null, visible: boolean }}
 */
export function resolveTicketIcon(ticket, iconConfig) {
  if (!iconConfig) return { iconUrl: null, visible: true };

  const typeSlug = slugifyTicketType(ticket.projectName, ticket.serviceType, ticket.itemName);
  const itemSlug = slugifyItemName(ticket.itemName);

  const typeConfig = iconConfig.types?.find(t => t.slug === typeSlug);
  const itemConfig = iconConfig.items?.find(i => i.slug === itemSlug);

  // A ticket is hidden if EITHER its type or its parent item is toggled off.
  // This must gate every return path — including ones where the type inherits
  // its icon from the item — so the per-type visibility toggle actually works.
  const parentVisible = (typeConfig?.visible !== false) && (itemConfig?.visible !== false);

  // 1. Check subtype rules on the type (first match wins)
  if (typeConfig?.subtypes?.length > 0) {
    for (const rule of typeConfig.subtypes) {
      if (matchesSubtypeRule(ticket, rule)) {
        return {
          iconUrl: rule.iconUrl || typeConfig.iconUrl || itemConfig?.iconUrl || iconConfig.defaultIconUrl || null,
          visible: parentVisible && (rule.visible !== false)
        };
      }
    }
  }

  // 2. Type-level icon (specific override)
  if (typeConfig?.iconUrl) {
    return {
      iconUrl: typeConfig.iconUrl,
      visible: parentVisible
    };
  }

  // 3. Check item-level subtype rules (first match wins)
  if (itemConfig?.subtypes?.length > 0) {
    for (const rule of itemConfig.subtypes) {
      if (matchesSubtypeRule(ticket, rule)) {
        return {
          iconUrl: rule.iconUrl || itemConfig.iconUrl || iconConfig.defaultIconUrl || null,
          visible: parentVisible && (rule.visible !== false)
        };
      }
    }
  }

  // 4. Item name group icon
  if (itemConfig?.iconUrl) {
    return {
      iconUrl: itemConfig.iconUrl,
      visible: parentVisible
    };
  }

  // 5. Default icon or colored dot
  return {
    iconUrl: iconConfig.defaultIconUrl || null,
    visible: parentVisible
  };
}

/**
 * Check if a ticket matches a subtype rule
 */
function matchesSubtypeRule(ticket, rule) {
  const fieldValue = String(ticket[rule.field] || '').toLowerCase();
  const ruleValue = String(rule.value || '').toLowerCase();

  switch (rule.condition) {
    case 'contains':
      return fieldValue.includes(ruleValue);
    case 'not_contains':
      return !fieldValue.includes(ruleValue);
    case 'equals':
      return fieldValue === ruleValue;
    case 'not_equals':
      return fieldValue !== ruleValue;
    default:
      return false;
  }
}

/**
 * Get the rating tier for a field rep
 * @param {number} rating - Rep rating (0-5)
 * @returns {{ tier: number, color: string, label: string }}
 */
export function getRepRatingTier(rating) {
  const r = parseFloat(rating) || 0;
  if (r >= 4.5) return { tier: 5, color: '#4CAF50', label: '5 stars' };
  if (r >= 3.5) return { tier: 4, color: '#8BC34A', label: '4 stars' };
  if (r >= 2.5) return { tier: 3, color: '#FFC107', label: '3 stars' };
  if (r >= 1.5) return { tier: 2, color: '#FF9800', label: '2 stars' };
  if (r >= 0.5) return { tier: 1, color: '#F44336', label: '1 star' };
  return { tier: 0, color: '#9E9E9E', label: 'No Rating' };
}
